// @ts-nocheck
// Sheet pra criar post no Estudo do Livro:
// - Reflexão (texto livre) — qualquer membro
// - Citação (trecho do livro + comentário opcional) — qualquer membro
// - Pergunta (texto da pergunta) — qualquer membro
// - Quiz (admin / membros se allow_member_quiz) — pergunta + opções, com ou sem gabarito
// - Caixa de Perguntas (admin) — chip redireciona pra tela full-screen dedicada
// - Insight Final (admin only) — encerra o marco atual ao publicar.
//   É o gesto cerimonial que sela uma fase da leitura.
//
// Tipos sem chip aqui: leader-answer (vem via Caixa de Perguntas → responder).
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../icons';
import { Sheet } from '../common';

export type PostKindCompose = 'reflection' | 'citation' | 'question' | 'quiz' | 'question-box' | 'insight-final';

const TYPE_META: Record<PostKindCompose, { icon: string; title: string; placeholder: string; subtitle: string; adminOnly?: boolean }> = {
  reflection:    { icon: '💡', title: 'Reflexão',       placeholder: 'O que esse trecho me fez pensar...',                   subtitle: 'Pensamento livre sobre o que leu' },
  citation:      { icon: '📖', title: 'Citação',        placeholder: 'Trecho que te marcou, copiado do livro...',           subtitle: 'Trecho do livro + seu comentário' },
  question:      { icon: '❓', title: 'Pergunta',       placeholder: 'O que você ainda não entendeu? O que provoca debate?', subtitle: 'Provoque o grupo' },
  quiz:          { icon: '🎲', title: 'Quiz',           placeholder: 'Enunciado da pergunta...',                              subtitle: 'Múltipla escolha — com ou sem gabarito', adminOnly: true },
  'question-box':{ icon: '📥', title: 'Caixa',          placeholder: '',                                                       subtitle: 'Receber dúvidas anônimas dos membros', adminOnly: true },
  'insight-final': { icon: '🎯', title: 'Insight Final', placeholder: 'O que carrego destas páginas que vão me acompanhar daqui pra frente...', subtitle: 'Encerra o marco atual e abre o próximo', adminOnly: true },
};

export const ComposeSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  term: 'clube' | 'grupo';
  defaultPage?: number;
  isAdmin?: boolean;
  // Permite membros (não-admins) criar quiz quando true (clubs.allow_member_quiz)
  allowMemberQuiz?: boolean;
  activeMilestoneLabel?: string;
  onSubmit: (payload: { kind: PostKindCompose; content: any; page?: number; parentPostId?: string }) => Promise<void>;
  // Quando passado, força reflection + injeta parentPostId no submit + mostra a
  // pergunta original em destaque pra dar contexto ao redator.
  replyTo?: { postId: string; questionText: string; authorName: string } | null;
  // Caixa de Perguntas tem tela dedicada full-screen (mais espaço pra config).
  // Click no chip 'question-box' fecha o sheet e dispara esse callback no App.tsx.
  onPickQuestionBox?: () => void;
}> = ({ open, onClose, term, defaultPage, isAdmin = false, allowMemberQuiz = false, activeMilestoneLabel, onSubmit, replyTo, onPickQuestionBox }) => {
  const [kind, setKind] = useState<PostKindCompose>('reflection');
  const [text, setText] = useState('');
  const [comment, setComment] = useState(''); // só pra citation
  const [title, setTitle] = useState(''); // só pra insight-final
  const [page, setPage] = useState<string>('');
  // Quiz state: título opcional + lista de perguntas (cada uma com opções) + flag gabarito.
  // Cada pergunta vira um question_id no backend (q1, q2, ...). Mínimo 1 pergunta,
  // máximo 20. Cada pergunta exige 2-6 opções.
  type QuizQ = { text: string; options: { text: string; correct?: boolean }[] };
  const newEmptyQuestion = (): QuizQ => ({ text: '', options: [{ text: '' }, { text: '' }] });
  const [quizTitle, setQuizTitle] = useState('');
  const [quizQuestions, setQuizQuestions] = useState<QuizQ[]>([newEmptyQuestion()]);
  const [quizHasGabarito, setQuizHasGabarito] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textoRef = useRef<HTMLTextAreaElement | null>(null);

  // O foco tem que vir de um EFEITO, não de `autoFocus`.
  //
  // O Sheet sempre renderiza os filhos — `open` só troca a classe que anima a
  // subida. Com `autoFocus` no textarea, ele focava no momento em que a PÁGINA
  // montava, e o teclado do celular abria sozinho ao entrar no Estudo, cobrindo
  // metade da tela sem ninguém ter pedido. E `autoFocus={open}` não resolveria:
  // o React só aplica autoFocus na montagem, e aqui a montagem acontece com o
  // sheet fechado.
  //
  // O atraso deixa a animação de subida acontecer antes; focar no mesmo quadro
  // faz o iOS abrir o teclado com o painel ainda no meio do caminho.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { try { textoRef.current && textoRef.current.focus(); } catch (_) {} }, 260);
    return () => clearTimeout(t);
  }, [open, kind]);

  useEffect(() => {
    if (!open) return;
    setText('');
    setComment('');
    setTitle('');
    setQuizTitle('');
    setQuizQuestions([newEmptyQuestion()]);
    setQuizHasGabarito(false);
    setPage(defaultPage ? String(defaultPage) : '');
    setError(null);
    // Em replyTo, força reflection (resposta a pergunta vira reflection com parent)
    setKind(replyTo ? 'reflection' : 'reflection');
  }, [open, defaultPage, replyTo]);

  // Se o user clicar no chip question-box, redireciona pra tela full-screen
  const handlePickKind = (k: PostKindCompose) => {
    if (k === 'question-box') {
      onClose();
      onPickQuestionBox?.();
      return;
    }
    setKind(k);
  };

  const meta = TYPE_META[kind];
  // Validação por tipo: quiz exige cada pergunta com enunciado + 2 opções.
  // Se tem gabarito, cada pergunta precisa ter uma opção marcada correta.
  const isQuiz = kind === 'quiz';
  const quizValid = isQuiz && quizQuestions.length >= 1 && quizQuestions.every(q => {
    const validOpts = q.options.filter(o => o.text.trim().length > 0);
    if (q.text.trim().length < 5) return false;
    if (validOpts.length < 2) return false;
    if (quizHasGabarito && !validOpts.some(o => o.correct)) return false;
    return true;
  });
  const minLen = kind === 'citation' ? 5 : kind === 'insight-final' ? 30 : 10;
  const maxLen = kind === 'reflection' || kind === 'insight-final' ? 4000 : kind === 'citation' ? 2000 : 1000;
  const valid = isQuiz
    ? quizValid
    : (text.trim().length >= minLen && text.trim().length <= maxLen);
  const isInsightFinal = kind === 'insight-final';

  // Helpers de manipulação do quiz (perguntas + opções)
  const updateQuizQuestion = (qi: number, patch: Partial<QuizQ>) => {
    setQuizQuestions(qs => qs.map((q, i) => i === qi ? { ...q, ...patch } : q));
  };
  const updateQuizOption = (qi: number, oi: number, patch: { text?: string; correct?: boolean }) => {
    setQuizQuestions(qs => qs.map((q, i) => {
      if (i !== qi) return q;
      const options = q.options.map((o, j) => j === oi ? { ...o, ...patch } : o);
      return { ...q, options };
    }));
  };
  const markOptionCorrect = (qi: number, oi: number) => {
    // Apenas 1 opção correta por pergunta (gabarito)
    setQuizQuestions(qs => qs.map((q, i) => {
      if (i !== qi) return q;
      const wasCorrect = q.options[oi]?.correct;
      return { ...q, options: q.options.map((o, j) => ({ ...o, correct: j === oi ? !wasCorrect : false })) };
    }));
  };
  const addQuizOption = (qi: number) => {
    setQuizQuestions(qs => qs.map((q, i) => i === qi && q.options.length < 6
      ? { ...q, options: [...q.options, { text: '' }] }
      : q));
  };
  const removeQuizOption = (qi: number, oi: number) => {
    setQuizQuestions(qs => qs.map((q, i) => i === qi && q.options.length > 2
      ? { ...q, options: q.options.filter((_, j) => j !== oi) }
      : q));
  };
  const addQuizQuestion = () => {
    if (quizQuestions.length >= 20) return;
    setQuizQuestions([...quizQuestions, newEmptyQuestion()]);
  };
  const removeQuizQuestion = (qi: number) => {
    if (quizQuestions.length <= 1) return;
    setQuizQuestions(quizQuestions.filter((_, i) => i !== qi));
  };

  const handleSubmit = async () => {
    if (!valid || saving) return;
    // Confirmação antes de fechar marco com Insight Final (gesto irreversível visualmente)
    if (isInsightFinal) {
      const ok = window.confirm(
        `Publicar o Insight Final encerra o marco "${activeMilestoneLabel || 'atual'}" e abre o próximo (se já existir). Continuar?`
      );
      if (!ok) return;
    }
    setError(null);
    const pageNum = parseInt(page, 10);
    const payload: any = { kind, content: {} };
    if (kind === 'reflection') payload.content = { text: text.trim() };
    if (kind === 'citation')   payload.content = { text: text.trim(), comment: comment.trim() || null };
    if (kind === 'question')   payload.content = { text: text.trim() };
    if (kind === 'insight-final') payload.content = {
      title: title.trim() || null,
      text: text.trim(),
      milestoneLabel: activeMilestoneLabel || null,
    };
    if (kind === 'quiz') {
      // Multi-pergunta: cada question vira q1, q2, ... e suas opções a/b/c/d/e/f.
      // Backend (validateAndNormalizeContent) re-valida; mandamos ids previsíveis.
      const questions = quizQuestions.map((q, qi) => ({
        id: `q${qi + 1}`,
        text: q.text.trim(),
        options: q.options
          .filter(o => o.text.trim())
          .slice(0, 6)
          .map((o, oi) => ({
            id: String.fromCharCode(97 + oi),
            text: o.text.trim(),
            ...(quizHasGabarito && o.correct ? { correct: true } : {}),
          })),
      }));
      payload.content = {
        title: quizTitle.trim() || null,
        questions,
        hasGabarito: !!quizHasGabarito,
      };
    }
    if (Number.isFinite(pageNum) && pageNum >= 0) payload.page = pageNum;
    // Resposta a uma pergunta: linka via parent_post_id no banco
    if (replyTo) payload.parentPostId = replyTo.postId;

    setSaving(true);
    try {
      await onSubmit(payload);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erro ao publicar');
    } finally {
      setSaving(false);
    }
  };

  const charsLeft = maxLen - text.length;

  return (
    <Sheet open={open} onClose={onClose} title={replyTo ? 'Responder pergunta' : 'Compartilhar no estudo'}>
      {/* Em modo reply: mostra a pergunta original em destaque (blockquote estilo) */}
      {replyTo && (
        <div style={{
          padding: '12px 14px', borderRadius: 12,
          background: 'var(--paper-2)',
          border: '1px dashed var(--line-strong)',
          marginTop: 4, marginBottom: 12,
        }}>
          <div className="mono" style={{
            fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: 'var(--ink-3)', fontWeight: 600, marginBottom: 4,
          }}>
            ❓ {replyTo.authorName} perguntou
          </div>
          <div className="serif" style={{
            fontSize: 15, lineHeight: 1.4, fontStyle: 'italic',
            color: 'var(--ink)',
          }}>"{replyTo.questionText}"</div>
        </div>
      )}

      {/* Picker de tipo — 3 públicos + quiz (admin/allowMember) + caixa (admin) + insight-final (admin).
          Esconde em modo reply. Caixa redireciona pra tela full-screen ao clicar. */}
      {!replyTo && <div style={{ display: 'flex', gap: 6, marginTop: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {/* Pergunta (kind 'question') removida — Caixa de Perguntas (admin)
            cobre o mesmo caso de uso de forma mais estruturada. */}
        {([
          'reflection', 'citation',
          ...((isAdmin || allowMemberQuiz) ? ['quiz' as const] : []),
          ...(isAdmin ? ['question-box' as const, 'insight-final' as const] : []),
        ] as PostKindCompose[]).map(k => {
          const m = TYPE_META[k];
          const sel = kind === k;
          const isFinal = k === 'insight-final';
          const isAdminBadge = m.adminOnly && !isFinal;
          return (
            <button key={k} onClick={() => handlePickKind(k)} style={{
              flex: '1 1 28%', minWidth: 70,
              padding: '10px 8px', borderRadius: 12,
              background: sel
                ? (isFinal ? 'linear-gradient(135deg, var(--ember), #E07A3B)' : 'var(--ember-soft)')
                : 'var(--paper-2)',
              border: '1px solid ' + (sel
                ? (isFinal ? 'var(--ember)' : 'var(--ember)')
                : 'var(--line)'),
              color: sel ? (isFinal ? 'var(--paper)' : 'var(--ember)') : 'var(--ink-2)',
              fontSize: 12, fontWeight: sel ? 600 : 500,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              position: 'relative',
            }}>
              <span style={{ fontSize: 18 }}>{m.icon}</span>
              {m.title}
              {isFinal && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  fontSize: 7.5, padding: '2px 5px', borderRadius: 4,
                  background: 'var(--ink)', color: 'var(--paper)',
                  fontFamily: 'Geist Mono, monospace',
                  letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600,
                }}>marco</span>
              )}
              {isAdminBadge && (
                <span style={{
                  position: 'absolute', top: -4, right: -4,
                  fontSize: 7.5, padding: '2px 5px', borderRadius: 4,
                  background: 'var(--ink-3)', color: 'var(--paper)',
                  fontFamily: 'Geist Mono, monospace',
                  letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600,
                }}>{k === 'quiz' && !isAdmin && allowMemberQuiz ? '' : 'líder'}</span>
              )}
            </button>
          );
        })}
      </div>}

      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8, fontStyle: 'italic' }}>
        {replyTo ? 'Sua resposta é uma reflexão pública vinculada à pergunta acima.' : meta.subtitle}
      </div>

      {isInsightFinal && activeMilestoneLabel && (
        <div style={{
          padding: '10px 14px', borderRadius: 12,
          background: 'color-mix(in oklab, var(--ember) 12%, var(--paper-2))',
          border: '1px solid var(--ember)',
          marginBottom: 12, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.45,
        }}>
          <strong style={{ color: 'var(--ember)' }}>✦ Encerrando marco:</strong> {activeMilestoneLabel}.
          <br/>
          <span style={{ color: 'var(--ink-3)' }}>
            O próximo marco vira o atual quando você publicar (se houver outro definido).
          </span>
        </div>
      )}

      {isInsightFinal && (
        <input
          value={title} onChange={e => setTitle(e.target.value.slice(0, 120))}
          placeholder="Título do insight (opcional)"
          style={{
            width: '100%', padding: 14, marginBottom: 8,
            borderRadius: 14, border: '1px solid var(--line)',
            background: 'var(--paper-2)', fontFamily: 'Instrument Serif, serif',
            fontSize: 18, color: 'var(--ink)', outline: 'none',
          }}
        />
      )}

      {/* UI específica do Quiz multi-pergunta */}
      {isQuiz ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Título do quiz (opcional) */}
          <input
            value={quizTitle}
            onChange={e => setQuizTitle(e.target.value.slice(0, 120))}
            placeholder="Título do quiz (opcional)"
            style={{
              width: '100%', padding: 12, borderRadius: 12,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              fontFamily: 'Instrument Serif, serif', fontSize: 17,
              outline: 'none', color: 'var(--ink)',
            }}
          />
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer', fontSize: 13, color: 'var(--ink-2)',
          }}>
            <input
              type="checkbox"
              checked={quizHasGabarito}
              onChange={e => {
                setQuizHasGabarito(e.target.checked);
                if (!e.target.checked) {
                  // Limpa "correct" de todas opções de todas perguntas
                  setQuizQuestions(qs => qs.map(q => ({ ...q, options: q.options.map(o => ({ ...o, correct: false })) })));
                }
              }}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span>Tem gabarito (revela a opção correta após cada resposta)</span>
          </label>

          {/* Lista de perguntas */}
          {quizQuestions.map((q, qi) => {
            const validOpts = q.options.filter(o => o.text.trim().length > 0);
            const missingCorrect = quizHasGabarito && validOpts.length >= 2 && !validOpts.some(o => o.correct);
            return (
              <div key={qi} style={{
                padding: 12, borderRadius: 14,
                border: '1px solid var(--line)',
                background: 'var(--paper)',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="mono" style={{
                    fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
                    color: 'var(--ember)', fontWeight: 600,
                  }}>Pergunta {qi + 1}{quizQuestions.length > 1 ? ` de ${quizQuestions.length}` : ''}</span>
                  {quizQuestions.length > 1 && (
                    <button
                      onClick={() => removeQuizQuestion(qi)}
                      title="Remover pergunta"
                      style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: 'transparent', border: '1px solid var(--line)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                      }}
                    >
                      <Icon name="trash" size={11} color="var(--ink-4)"/>
                    </button>
                  )}
                </div>
                <textarea
                  value={q.text}
                  onChange={e => updateQuizQuestion(qi, { text: e.target.value.slice(0, 500) })}
                  placeholder="Enunciado da pergunta..."
                  rows={2}
                  style={{
                    width: '100%', padding: 12, borderRadius: 10,
                    background: 'var(--paper-2)', border: '1px solid var(--line)',
                    fontFamily: 'inherit', fontSize: 14, lineHeight: 1.4, resize: 'vertical',
                    outline: 'none', color: 'var(--ink)',
                  }}
                />
                {q.options.map((opt, oi) => (
                  <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="mono" style={{
                      width: 22, textAlign: 'center', color: 'var(--ink-3)', fontSize: 11,
                    }}>{String.fromCharCode(97 + oi).toUpperCase()}.</span>
                    <input
                      value={opt.text}
                      onChange={e => updateQuizOption(qi, oi, { text: e.target.value.slice(0, 200) })}
                      placeholder={`Opção ${oi + 1}`}
                      style={{
                        flex: 1, padding: '8px 10px', borderRadius: 10,
                        background: 'var(--paper-2)', border: '1px solid var(--line)',
                        fontSize: 13.5, outline: 'none', color: 'var(--ink)', fontFamily: 'inherit',
                      }}
                    />
                    {quizHasGabarito && (
                      <button
                        onClick={() => markOptionCorrect(qi, oi)}
                        title={opt.correct ? 'Marcada como correta' : 'Marcar como correta'}
                        style={{
                          width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                          background: opt.correct ? 'var(--sage)' : 'var(--paper-2)',
                          border: '1px solid ' + (opt.correct ? 'var(--sage)' : 'var(--line)'),
                          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                      >
                        <Icon name="check" size={12} color={opt.correct ? 'var(--paper)' : 'var(--ink-4)'}/>
                      </button>
                    )}
                    {q.options.length > 2 && (
                      <button
                        onClick={() => removeQuizOption(qi, oi)}
                        title="Remover opção"
                        style={{
                          width: 28, height: 28, borderRadius: 10, flexShrink: 0,
                          background: 'transparent', border: '1px solid var(--line)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                      >
                        <Icon name="trash" size={11} color="var(--ink-4)"/>
                      </button>
                    )}
                  </div>
                ))}
                {q.options.length < 6 && (
                  <button
                    onClick={() => addQuizOption(qi)}
                    style={{
                      marginTop: 2, padding: '6px 10px', borderRadius: 10,
                      background: 'transparent', border: '1px dashed var(--line-strong)',
                      fontSize: 11.5, color: 'var(--ink-3)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <Icon name="plus" size={11} color="var(--ink-3)"/> opção
                  </button>
                )}
                {missingCorrect && (
                  <div style={{ fontSize: 11, color: 'var(--ember)', fontStyle: 'italic' }}>
                    Marque a opção correta com o ✓ verde.
                  </div>
                )}
              </div>
            );
          })}
          {quizQuestions.length < 20 && (
            <button
              onClick={addQuizQuestion}
              style={{
                padding: '10px 12px', borderRadius: 12,
                background: 'var(--ember-soft)', border: '1px dashed var(--ember)',
                fontSize: 13, color: 'var(--ember)', fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <Icon name="plus" size={13} color="var(--ember)"/> Adicionar pergunta
            </button>
          )}
        </div>
      ) : (
      <textarea
        ref={textoRef}
        value={text}
        onChange={e => setText(e.target.value.slice(0, maxLen))}
        placeholder={meta.placeholder}
        style={{
          width: '100%', minHeight: kind === 'reflection' ? 140 : 100,
          padding: 14, borderRadius: 14,
          background: 'var(--paper-2)',
          border: '1px solid var(--line)',
          fontFamily: 'inherit',
          fontSize: 15, lineHeight: 1.5, resize: 'none',
          outline: 'none', color: 'var(--ink)',
        }}
      />
      )}

      {kind === 'citation' && (
        <>
          <div className="mono" style={{
            marginTop: 12, fontSize: 9.5, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 600,
          }}>Seu comentário (opcional)</div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value.slice(0, 1000))}
            placeholder="Por que essa frase te marcou..."
            style={{
              width: '100%', minHeight: 70, marginTop: 6,
              padding: 12, borderRadius: 12,
              background: 'var(--paper-2)',
              border: '1px solid var(--line)',
              fontSize: 14, lineHeight: 1.4, resize: 'none',
              outline: 'none', color: 'var(--ink)', fontFamily: 'inherit',
            }}
          />
        </>
      )}

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>pág</span>
          <input
            type="number" inputMode="numeric" min={0}
            value={page} onChange={e => setPage(e.target.value)}
            placeholder="—"
            style={{
              width: 64, padding: '6px 10px', borderRadius: 8,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              fontFamily: 'Geist Mono, monospace',
              fontSize: 13, color: 'var(--ink)', outline: 'none', textAlign: 'center',
            }}
          />
        </div>
        {!isQuiz && (
          <span className="mono" style={{
            fontSize: 9.5, letterSpacing: '0.06em',
            color: charsLeft < 100 ? 'var(--ember)' : 'var(--ink-4)',
          }}>
            {charsLeft} restantes
          </span>
        )}
      </div>

      {error && (
        <div style={{
          marginTop: 10, padding: '10px 12px', borderRadius: 10,
          background: 'var(--ember-soft)', color: 'var(--ember)',
          fontSize: 12, fontWeight: 500,
        }}>{error}</div>
      )}

      <button onClick={handleSubmit} disabled={!valid || saving} style={{
        width: '100%', marginTop: 16, padding: '14px', borderRadius: 14,
        background: !valid || saving
          ? 'var(--paper-3)'
          : (isInsightFinal ? 'linear-gradient(135deg, var(--ember), #E07A3B)' : 'var(--ember)'),
        color: !valid || saving ? 'var(--ink-4)' : 'var(--paper)',
        fontSize: 15, fontWeight: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: !valid || saving ? 'none' : '0 12px 24px -10px rgba(199,83,26,0.55)',
        cursor: !valid || saving ? 'not-allowed' : 'pointer',
      }}>
        {saving ? 'Publicando…' : isInsightFinal ? '🎯 Publicar e encerrar marco' : `Publicar ${meta.title.toLowerCase()}`}
        {!saving && valid && !isInsightFinal && <Icon name="arrow-right" size={15} color="var(--paper)" stroke={2.4}/>}
      </button>

      <div className="mono" style={{
        textAlign: 'center', marginTop: 10,
        fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--ink-4)',
      }}>
        {isInsightFinal
          ? `Encerra o marco e abre o próximo · todos os membros do ${term} vêem`
          : `Aparece pra todos os membros do ${term}`}
      </div>
    </Sheet>
  );
};
